import apiService from './api';
import { getAdvisorHeaders } from './spray';
import type { ApiResponse } from '../types/api';
import type { ChemicalInventoryLocation } from '../types/spray';
import type {
  PurchaseOrder, POLineItem, POReceipt, PODocument, Supplier, SupplierInput, XeroSupplierCandidate,
} from '../types/purchaseOrders';
import type { XeroAccount, XeroContactSummary, XeroTaxRate } from '../types/nurseryAccounts';
import { openDocument } from '../utils/openDocument';

/**
 * Purchase orders, their suppliers, and who uses them.
 *
 * They belong to no module: an Accounts Admin says who raises them, and an
 * Accounts seat reads them and keeps the supplier list. The API is
 * `/purchase-orders`; `/spray/purchase-orders`, from when they were a tab of
 * Spray, still answers for app bundles already on devices.
 *
 * The order calls carry the advisor header, so a spray advisor acting for
 * another organisation raises its orders as before.
 */

const BASE = '/purchase-orders';

export interface PurchaseOrderAccess {
  /** Authorised by an Accounts Admin: raise, send, receive, cancel. */
  can_raise: boolean;
  /** Authorised, or holding an Accounts seat. */
  can_read: boolean;
  /** Raises orders, or holds a Retail seat: receives deliveries. Absent from an older server. */
  can_receive?: boolean;
  /** An Accounts Admin: says who is authorised. */
  can_manage: boolean;
  /** An Accounts seat: adds and edits suppliers. Absent from an older server. */
  can_edit_suppliers?: boolean;
}

export interface PurchaseOrderPerson {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  authorised: boolean;
}

/** Purchase orders to the organisation's own Xero, as the Xero page shows it. */
export interface PurchaseOrderXeroSettings {
  /** Xero runs here at all (production only). */
  enabled_here: boolean;
  /** Xero is connected with the permissions purchase orders need. */
  connected: boolean;
  /** Orders are sent to Xero. */
  enabled: boolean;
  account_code: string | null;
  tax_type: string | null;
  /** The Xero bank account card purchases go out of as Spend Money. */
  card_account_id?: string | null;
  card_account_name?: string | null;
  /** The Xero contact orders to a supplier not on the list go to. */
  sundry_contact_id?: string | null;
  sundry_contact_name?: string | null;
  /** The connection may write Spend Money, and attach a receipt to it. */
  can_post_card?: boolean;
  can_attach?: boolean;
  queue: { pending: number; failed: number; billed: number };
  failures: { id: number; reference_number: string | null; supplier_name: string | null; xero_sync_error: string | null }[];
  /** After switching on: how many orders already out went to Xero. */
  queued?: number;
}

/** A bank account in the organisation's Xero: where a card purchase is paid from. */
export interface XeroBankAccount {
  account_id: string;
  code: string | null;
  name: string;
  is_credit_card: boolean;
}

/** One order's Xero side, as its page shows it. */
export interface PurchaseOrderXero {
  /** Orders go to Xero here: switched on, connected, the live system. */
  active: boolean;
  /** Goes as Spend Money from the card account. Absent from an older server. */
  paid_by_card?: boolean;
  xero_bank_transaction_id?: string | null;
  /** The supplier is on the organisation's list; otherwise it goes to the sundry contact. */
  on_supplier_list?: boolean;
  sundry_contact_name?: string | null;
  xero_purchase_order_id: string | null;
  sync_status: 'pending' | 'pushing' | 'synced' | 'failed' | null;
  sync_error: string | null;
  synced_at: string | null;
  /** Xero's own: AUTHORISED, BILLED, DELETED, … */
  xero_status: string | null;
  supplier_name: string | null;
  contact_id: string | null;
  contact: XeroContactSummary | null;
  contact_error: string | null;
}

export interface PurchaseOrderLineInput {
  item_type?: 'chemical' | 'general' | 'nursery';
  chemical_id?: number | null;
  /** A nursery line: one of the organisation's stock items. */
  stock_item_id?: number | null;
  item_description?: string | null;
  quantity_ordered: number;
  unit?: string;
  unit_cost?: number | null;
  notes?: string | null;
}

export interface PurchaseOrderInput {
  farm_id?: number | null;
  /** One of the organisation's suppliers. */
  supplier_id?: number | null;
  /** Typed, when no supplier from the list is chosen. */
  supplier_name?: string | null;
  ordered_date?: string | null;
  expected_delivery_date?: string | null;
  notes?: string | null;
  line_items?: PurchaseOrderLineInput[];
  /** Paid on the company card: goes to Xero as Spend Money, not a purchase order. */
  paid_by_card?: boolean;
}

class PurchaseOrderService {
  // ── Orders ───────────────────────────────────────────────────────────────

  async list(filters?: {
    status?: string;
    /** A farm's orders, or 'none' for orders raised for no farm. */
    farm_id?: number | 'none';
    supplier_id?: number;
    chemical_id?: number;
    page?: number;
    limit?: number;
    date_from?: string;
    date_to?: string;
  }): Promise<ApiResponse<{ pos: PurchaseOrder[]; total: number; page: number; totalPages: number }>> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.farm_id) params.set('farm_id', String(filters.farm_id));
    if (filters?.supplier_id) params.set('supplier_id', String(filters.supplier_id));
    if (filters?.chemical_id) params.set('chemical_id', String(filters.chemical_id));
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.date_from) params.set('date_from', filters.date_from);
    if (filters?.date_to) params.set('date_to', filters.date_to);
    const qs = params.toString();
    return apiService.get(`${BASE}${qs ? `?${qs}` : ''}`, getAdvisorHeaders());
  }

  async get(id: number): Promise<ApiResponse<PurchaseOrder>> {
    return apiService.get(`${BASE}/${id}`, getAdvisorHeaders());
  }

  async create(data: PurchaseOrderInput): Promise<ApiResponse<PurchaseOrder>> {
    return apiService.post(BASE, data, getAdvisorHeaders());
  }

  async update(id: number, data: Omit<PurchaseOrderInput, 'farm_id' | 'line_items'> & { status?: string }): Promise<ApiResponse<PurchaseOrder>> {
    return apiService.put(`${BASE}/${id}`, data, getAdvisorHeaders());
  }

  async send(id: number): Promise<ApiResponse<{ sent_to: string }>> {
    // Builds the PDF and emails it: well past the 10 s default on a slow day.
    return apiService.post(`${BASE}/${id}/send`, {}, getAdvisorHeaders(), { timeout: 45000 });
  }

  async addLineItem(poId: number, data: PurchaseOrderLineInput): Promise<ApiResponse<POLineItem>> {
    return apiService.post(`${BASE}/${poId}/line-items`, data, getAdvisorHeaders());
  }

  async updateLineItem(poId: number, lineId: number, data: Partial<POLineItem>): Promise<ApiResponse<POLineItem>> {
    return apiService.put(`${BASE}/${poId}/line-items/${lineId}`, data, getAdvisorHeaders());
  }

  async deleteLineItem(poId: number, lineId: number): Promise<ApiResponse<{ deleted: boolean }>> {
    return apiService.delete(`${BASE}/${poId}/line-items/${lineId}`, undefined, getAdvisorHeaders());
  }

  async receiveStock(poId: number, data: {
    notes?: string | null;
    freight_ex_gst?: number | null;
    items: Array<{
      line_item_id: number;
      chemical_id?: number | null;
      quantity_received: number;
      location_id?: number | null;
      batch_number?: string | null;
      expiry_date?: string | null;
      barcode_scanned?: string | null;
      unit_cost?: number | null;
      notes?: string | null;
      nursery_location_id?: number | null;
    }>;
  }): Promise<ApiResponse<POReceipt>> {
    return apiService.post(`${BASE}/${poId}/receive`, data, getAdvisorHeaders());
  }

  async getStockReceipts(poId: number): Promise<ApiResponse<POReceipt[]>> {
    return apiService.get(`${BASE}/${poId}/receipts`, getAdvisorHeaders());
  }

  // ── Suppliers ────────────────────────────────────────────────────────────

  /** The organisation's suppliers. Anyone who reads or raises orders. */
  async listSuppliers(opts?: { search?: string; includeInactive?: boolean }): Promise<ApiResponse<Supplier[]>> {
    const params = new URLSearchParams();
    if (opts?.search) params.set('search', opts.search);
    if (opts?.includeInactive) params.set('include_inactive', 'true');
    const qs = params.toString();
    return apiService.get(`${BASE}/suppliers${qs ? `?${qs}` : ''}`, getAdvisorHeaders());
  }

  /** Any accounts seat. */
  async createSupplier(data: SupplierInput & { name: string }): Promise<ApiResponse<Supplier>> {
    return apiService.post(`${BASE}/suppliers`, data);
  }

  /** Any accounts seat. `active: false` retires it. */
  async updateSupplier(id: number, data: SupplierInput): Promise<ApiResponse<Supplier>> {
    return apiService.put(`${BASE}/suppliers/${id}`, data);
  }

  /** Any accounts seat: the supplier contacts in Xero (those that have had a bill). */
  async listXeroSuppliers(): Promise<ApiResponse<XeroSupplierCandidate[]>> {
    // Xero pages its contacts; a long list takes a few round trips.
    return apiService.get(`${BASE}/suppliers/xero`, { timeout: 60000 });
  }

  /** Any accounts seat: put these Xero suppliers on the list, linked to their contacts. */
  async importXeroSuppliers(contactIds: string[]): Promise<ApiResponse<{ created: number; linked: number; skipped: number }>> {
    return apiService.post(`${BASE}/suppliers/xero`, { contact_ids: contactIds }, undefined, { timeout: 60000 });
  }

  // ── Who uses them ────────────────────────────────────────────────────────

  async getAccess(): Promise<ApiResponse<PurchaseOrderAccess>> {
    return apiService.get(`${BASE}/access`);
  }

  /** Any accounts seat: the organisation's people, each with whether they are authorised. */
  async getPeople(): Promise<ApiResponse<PurchaseOrderPerson[]>> {
    return apiService.get(`${BASE}/authorised-users`);
  }

  /** Accounts Admin only: replace the authorised people. */
  async setAuthorised(userIds: number[]): Promise<ApiResponse<PurchaseOrderPerson[]>> {
    return apiService.put(`${BASE}/authorised-users`, { user_ids: userIds });
  }

  // ── Payment receipts ─────────────────────────────────────────────────────

  /** Attach a payment receipt (a photo or PDF) to an order. */
  async addReceipt(poId: number, file: File, note?: string): Promise<ApiResponse<PODocument>> {
    const form = new FormData();
    form.append('file', file);
    if (note) form.append('note', note);
    // A phone photo over paddock signal: well past the default 10 s.
    return apiService.post(`${BASE}/${poId}/documents`, form, undefined, { timeout: 120000 });
  }

  async removeReceipt(docId: number): Promise<ApiResponse<{ id: number; deleted: boolean }>> {
    return apiService.delete(`${BASE}/documents/${docId}`);
  }

  /** The route needs the bearer token, so the file cannot be a plain link. */
  async openReceipt(doc: Pick<PODocument, 'id' | 'file_name'>): Promise<void> {
    await openDocument(`${BASE}/documents/${doc.id}/file`, doc.file_name || `receipt-${doc.id}`);
  }

  // ── Xero ─────────────────────────────────────────────────────────────────

  /** Purchase orders to Xero: any accounts seat reads, an Accounts Admin saves. */
  async getXeroSettings(): Promise<ApiResponse<PurchaseOrderXeroSettings>> {
    return apiService.get(`${BASE}/xero-settings`);
  }

  async saveXeroSettings(settings: {
    enabled?: boolean; account_code?: string | null; tax_type?: string | null;
    card_account_id?: string | null; sundry_contact_id?: string | null;
  }): Promise<ApiResponse<PurchaseOrderXeroSettings>> {
    return apiService.put(`${BASE}/xero-settings`, settings);
  }

  /** Accounts Admin: Xero's accounts, the tax rates an expense can carry, and its bank accounts. */
  async getXeroLists(): Promise<ApiResponse<{ accounts: XeroAccount[]; tax_rates: XeroTaxRate[]; bank_accounts?: XeroBankAccount[] }>> {
    return apiService.get(`${BASE}/xero-settings/lists`);
  }

  async getXero(poId: number): Promise<ApiResponse<PurchaseOrderXero>> {
    return apiService.get(`${BASE}/${poId}/xero`);
  }

  /** Send it again after a failure. Waits for the attempt: Xero can be slow. */
  async retryXero(poId: number): Promise<ApiResponse<PurchaseOrderXero>> {
    return apiService.post(`${BASE}/${poId}/xero/retry`, {}, undefined, { timeout: 60000 });
  }

  /** Accounts Admin: the Xero contact this order's supplier goes to, or null to remove the link. */
  async setSupplierXeroContact(poId: number, contactId: string | null): Promise<ApiResponse<PurchaseOrderXero>> {
    return apiService.put(`${BASE}/${poId}/xero/contact`, { contact_id: contactId });
  }

  /** Where received chemical stock can be put. */
  async getLocations(): Promise<ApiResponse<ChemicalInventoryLocation[]>> {
    return apiService.get(`${BASE}/locations`);
  }
}

export default new PurchaseOrderService();
